<script lang="ts" setup>
  import type { Component } from 'vue'
  import { computed, onMounted, ref, toRefs } from 'vue'

  import { type AllScopesType, type SearchResultEffortGroups, useGeneratedEffortTypeStore } from '@/stores'
  import TabPanel from 'primevue/tabpanel'
  import PreviewPartner from '@/components/global-search/PreviewPartner.vue'
  import PreviewOrder from '@/components/global-search/PreviewOrder.vue'
  import PreviewServicecall from '@/components/global-search/PreviewServicecall.vue'
  import Dialog from 'primevue/dialog'
  import ListDescription from '@/elements/ListDescription.vue'
  import DevDump from '@/elements/DevDump.vue'
  // import OrderIcon from 'vue-material-design-icons/ReceiptTextOutline.vue'
  import { OrderIcon, PartnerIcon, ServiceCallIcon } from '@/config/icons'
  import Popover from 'primevue/popover'
  import Tabs from 'primevue/tabs'
  import TabList from 'primevue/tablist'
  import Tab from 'primevue/tab'
  import TabPanels from 'primevue/tabpanels'
  import { EffortJsonldVueGlobalSearchDefaultReadSync, EffortTypeJsonldDefaultReadSync } from '@/api'

  const supportedScopes: AllScopesType[] = ['ORDER', 'SERVICECALL', 'PARTNERCOLLECTOR']

  interface Props {
    results?: SearchResultEffortGroups
    target: HTMLElement
    searchedScope?: AllScopesType | undefined
    popUpClass?: string
    event?: Event | undefined
  }

  const lockList = defineModel<boolean>('lock')

  defineExpose({
    hide: () => {
      overlayRef.value?.hide()
    },
    show: (event: Event) => {
      overlayRef.value?.show(event)
    }
  })

  const props = defineProps<Props>()
  const { results, searchedScope, popUpClass, target } = toRefs(props)
  const emit = defineEmits<{
    resultSelected: [EffortJsonldVueGlobalSearchDefaultReadSync]
    shortcutClicked: []
    overlayHide: []
    overlayShow: []
    popUpClass: undefined
  }>()

  function getIcon(type: AllScopesType): Component | undefined {
    switch (type) {
      case 'PARTNERCOLLECTOR':
        return PartnerIcon

      case 'ORDER':
        return OrderIcon

      case 'SERVICECALL':
        return ServiceCallIcon
    }
    return undefined
  }

  const overlayRef = ref<InstanceType<typeof Popover>>()
  const allEffortTypes = ref<Record<string, EffortTypeJsonldDefaultReadSync>>({})

  onMounted(async () => {
    allEffortTypes.value = await useGeneratedEffortTypeStore()
      .findAllAsIriDictionary({})
      .then((d) => d.value)
    if (props.event)
      overlayRef.value?.show(props.event, props.target)
    else
      overlayRef.value?.show(new Event('fake'), props.target)
    emit('overlayShow')
  })

  function onEffortSelected(e: EffortJsonldVueGlobalSearchDefaultReadSync) {
    emit('resultSelected', e)
  }

  function onShortcutClicked() {
    emit('shortcutClicked')
  }

  const effortSubtitleOverlayShown = ref(false)
  const effortSubtitleOverlayData = ref<EffortJsonldVueGlobalSearchDefaultReadSync>()

  const resultGroups = computed(() => Object.keys(results.value ?? {}))

  function showOverlay(eff: EffortJsonldVueGlobalSearchDefaultReadSync) {
    lockList.value = true
    effortSubtitleOverlayData.value = eff
    effortSubtitleOverlayShown.value = true
  }

  function closeOverlay() {
    lockList.value = false
    effortSubtitleOverlayData.value = undefined
    effortSubtitleOverlayShown.value = false
  }

  const hasResults = computed<boolean>(() => {
    return 0 != (results.value?.ORDER?.length ?? 0) + (results.value?.PARTNERCOLLECTOR?.length ?? 0) + (results.value?.SERVICECALL?.length ?? 0)
  })

  const EmptyComponent = defineComponent({
    name: 'EmptyComponent',
    setup() {
      return () => h('div')
    }
  })

  function getPreviewComponent(key: AllScopesType): Component {
    switch (key) {
      case 'ORDER':
        return PreviewOrder
      case 'SERVICECALL':
        return PreviewServicecall
      case 'PARTNERCOLLECTOR':
        return PreviewPartner
      case 'EMPLOYEE':
      case 'MODULE':
      case 'INFOBOARD':
      case 'EFFORTESTIMATION':
      case 'REQUEST':
        return EmptyComponent
    }
  }
</script>

<template>
  <Popover
    ref="overlayRef"
    :dismissable="!lockList"
    append-to="#alternate-main"
    :class="popUpClass ? popUpClass : 'w-full p-0 md:p-2 md:w-11/12 mt-2 mx-auto'"
    show-close-icon
    @hide="$emit('overlayHide')">
    <Tabs
      v-if="hasResults"
      :value="0"
      scrollable>
      <TabList>
        <Tab
          v-for="(g, idx) in resultGroups.filter((gr) => results![gr].length) as AllScopesType[]"
          :key="g"
          :value="idx"
          class="flex items-center text-md py-[6px] px-3 !border rounded-md">
          <component
            :is="getIcon(g)"
            v-if="getIcon(g)"
            class="size-6 mr-1" />
          <span>{{ $t(`effort_types.${g}`) }}</span>
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel
          v-for="(g, idx) in resultGroups.filter((gr) => results![gr].length) as AllScopesType[]"
          :key="g"
          :value="idx"
          class="p-0">
          <component
            :is="getPreviewComponent(g)"
            v-for="result in results?.[g] ?? []"
            :key="result['@id']"
            :data="result"
            class="mt-1"
            @show-overlay="showOverlay"
            @effort-selected="onEffortSelected"
            @shortcut-clicked="onShortcutClicked" />
        </TabPanel>
      </TabPanels>
    </Tabs>
    <div
      v-else-if="searchedScope && !supportedScopes.includes(searchedScope)"
      class="flex h-full items-center pl-2">
      (Dieser Suchbereich wird noch nicht unterstützt)
    </div>
    <div
      v-else
      class="flex h-full items-center pl-2 w-full">
      {{ $t('global search.No results found') }}
    </div>

    <Dialog
      v-if="effortSubtitleOverlayData"
      v-model:visible="effortSubtitleOverlayShown"
      :dismissable-mask="false"
      header="Objekt-Schnelldetails"
      modal
      @after-hide="closeOverlay">
      <ListDescription
        :items="[
          { title: 'Titrop-Id:', description: effortSubtitleOverlayData.id },
          { title: 'Geschlossen:', description: effortSubtitleOverlayData.locked ? 'JA' : 'nein' },
          { title: 'Objekt-Art:', description: allEffortTypes[effortSubtitleOverlayData.type!].title },
          { title: 'Titel:', html: true, description: effortSubtitleOverlayData.title },
          { title: 'Untertitel:', html: true, description: effortSubtitleOverlayData.subtitle },
          { title: 'Belegnr.:', description: effortSubtitleOverlayData.externalDocReference },
          { title: 'Belegdatum:', description: $formatter.DateTime(effortSubtitleOverlayData.externalDocCreationDate) }
        ]"
        small />
      <DevDump :value="effortSubtitleOverlayData" />
    </Dialog>
  </Popover>
</template>

<style scoped>
  :deep(.p-tabpanels) {
    padding: 0;
  }
</style>
